import { formatValue, getNumberWithCommas, ValueTypes } from '@cfra-nextgen-frontend/shared/src/utils/valuesFormatter';
import type { ShoutTnaPoint } from '../api/chartDataTypes';
import { formatCurrencyInt, formatLongDate } from './perf10kFormat';

export type ShoutTnaSummary = {
    /** Plain-text version — used by the NVDA live region. */
    text: string;
    /** Same content with `shares` and `tna` wrapped in <strong> for the visual
     *  banner. Composed entirely from internal formatters (date string, scaled
     *  number, currency-prefixed integer) — no user-controlled input — so the
     *  shell can splice it via dangerouslySetInnerHTML without sanitization. */
    html: string;
};

/**
 * "May 22, 2026: Shares outstanding: 3.45M. Total net assets: USD 175,613,593."
 *
 * Both numeric values are rounded to integers — matches the perf10k convention
 * (`formatCurrencyInt` rounds) and reads cleanly through NVDA. The shares
 * outstanding figure has no currency prefix; total net assets carries the
 * payload's currency code.
 */
export function buildShoutTnaSummarySentence(point: ShoutTnaPoint, currency: string): ShoutTnaSummary {
    const date = formatLongDate(point.date);
    const sharesRounded = Math.round(point.shares_outstanding);
    // Visual: scaled (e.g. "3.45M") for compact display.
    const sharesHtml = formatValue({ value: sharesRounded, formattingType: ValueTypes.MillionsScale });
    // NVDA: comma-separated so screen readers speak it as a natural number
    // ("three million four hundred fifty thousand") instead of digit-by-digit.
    // Matches how `formatCurrencyInt` formats TNA below.
    const sharesText = getNumberWithCommas(sharesRounded);
    const tna = formatCurrencyInt(point.total_net_assets, currency);
    const tnaText = getNumberWithCommas(Math.round(point.total_net_assets));
    return {
        text: `As per ${date}: Shares outstanding: ${sharesText}. Total net assets: ${currency} ${tnaText}.`,
        html: `${date}: Shares outstanding: <strong>${sharesHtml}</strong>. Total net assets: <strong>${tna}</strong>.`,
    };
}
